import { LEAD_STATUSES, type LeadStatus, type PartnerListItem } from '@erp/shared';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useUpdateLeadStage } from '@/features/partners/api/use-partners';
import { formatDate, formatMoney } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const DRAG_MIME = 'application/x-erp-lead';

export interface LeadKanbanBoardProps {
  rows: PartnerListItem[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onOpen: (id: string) => void;
  canWrite: boolean;
}

interface PendingMove {
  partner: PartnerListItem;
  target: LeadStatus;
}

export function LeadKanbanBoard({
  rows,
  isLoading,
  isError,
  onRetry,
  onOpen,
  canWrite,
}: LeadKanbanBoardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const updateStage = useUpdateLeadStage();
  const [dragOverColumn, setDragOverColumn] = useState<LeadStatus | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
  const [lostReason, setLostReason] = useState('');

  const columns = useMemo(() => {
    const grouped = new Map<LeadStatus, PartnerListItem[]>();
    for (const status of LEAD_STATUSES) {
      grouped.set(status, []);
    }
    for (const row of rows) {
      const status: LeadStatus = row.leadStatus ?? 'NEW';
      grouped.get(status)?.push(row);
    }
    return LEAD_STATUSES.map((status) => ({ status, items: grouped.get(status) ?? [] }));
  }, [rows]);

  const applyMove = (partner: PartnerListItem, target: LeadStatus, reason: string | null): void => {
    updateStage.mutate(
      {
        id: partner.id,
        input:
          target === 'LOST' ? { leadStatus: target, lostReason: reason } : { leadStatus: target },
      },
      {
        onSuccess: () => {
          toast.success(
            t('partners.pipeline.moved', {
              name: partner.displayName,
              stage: t(`partners.leadStatuses.${target}`),
            }),
          );
          setPendingMove(null);
          setLostReason('');
        },
        onError: (error) =>
          toast.error(t('partners.pipeline.moveError'), { description: error.message }),
      },
    );
  };

  const requestMove = (partner: PartnerListItem, target: LeadStatus): void => {
    if (!canWrite || (partner.leadStatus ?? 'NEW') === target) {
      return;
    }
    if (target === 'LOST') {
      setLostReason('');
      setPendingMove({ partner, target });
      return;
    }
    applyMove(partner, target, null);
  };

  const moveByOffset = (partner: PartnerListItem, offset: number): void => {
    const current = LEAD_STATUSES.indexOf(partner.leadStatus ?? 'NEW');
    const next = LEAD_STATUSES[current + offset];
    if (next !== undefined) {
      requestMove(partner, next);
    }
  };

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3 rounded-md border border-border p-6">
        <p className="text-sm text-muted-foreground">{t('partners.states.error')}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7" aria-busy="true">
        {LEAD_STATUSES.map((status) => (
          <Skeleton key={status} className="h-64 w-full" />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7">
        {columns.map(({ status, items }) => (
          <section
            key={status}
            aria-label={t(`partners.leadStatuses.${status}`)}
            className={cn(
              'flex min-h-64 flex-col gap-2 rounded-md border border-border bg-muted/30 p-2 transition-colors',
              dragOverColumn === status && 'border-primary bg-accent',
            )}
            onDragOver={(event) => {
              if (!canWrite) {
                return;
              }
              event.preventDefault();
              setDragOverColumn(status);
            }}
            onDragLeave={() => setDragOverColumn(null)}
            onDrop={(event) => {
              event.preventDefault();
              setDragOverColumn(null);
              const id = event.dataTransfer.getData(DRAG_MIME);
              const partner = rows.find((row) => row.id === id);
              if (partner !== undefined) {
                requestMove(partner, status);
              }
            }}
          >
            <header className="flex items-center justify-between gap-2 px-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t(`partners.leadStatuses.${status}`)}
              </h3>
              <Badge variant="outline">{items.length}</Badge>
            </header>

            {items.length === 0 ? (
              <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                {t('partners.pipeline.emptyColumn')}
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {items.map((item) => (
                  <li key={item.id}>
                    <article
                      draggable={canWrite}
                      onDragStart={(event) => {
                        event.dataTransfer.setData(DRAG_MIME, item.id);
                        event.dataTransfer.effectAllowed = 'move';
                      }}
                      className="flex flex-col gap-2 rounded-md border border-border bg-background p-2"
                    >
                      <button
                        type="button"
                        className="text-left text-sm font-medium hover:underline"
                        onClick={() => onOpen(item.id)}
                      >
                        {item.displayName}
                      </button>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {item.partnerNumber}
                      </span>
                      <dl className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                        <div className="flex justify-between gap-2">
                          <dt>{t('partners.columns.dealSizeEstimated')}</dt>
                          <dd className="text-foreground">
                            {formatMoney(item.dealSizeEstimated, 'EUR', i18n.language)}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt>{t('partners.columns.winProbability')}</dt>
                          <dd className="text-foreground">
                            {item.winProbability === null
                              ? '—'
                              : `${String(item.winProbability)} %`}
                          </dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt>{t('partners.columns.expectedClosingDate')}</dt>
                          <dd className="text-foreground">
                            {formatDate(item.expectedClosingDate, i18n.language)}
                          </dd>
                        </div>
                      </dl>
                      {canWrite ? (
                        <div className="flex justify-between gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('partners.pipeline.movePrevious', {
                              name: item.displayName,
                            })}
                            disabled={LEAD_STATUSES.indexOf(item.leadStatus ?? 'NEW') === 0}
                            onClick={() => moveByOffset(item, -1)}
                          >
                            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('partners.pipeline.moveNext', { name: item.displayName })}
                            disabled={
                              LEAD_STATUSES.indexOf(item.leadStatus ?? 'NEW') ===
                              LEAD_STATUSES.length - 1
                            }
                            onClick={() => moveByOffset(item, 1)}
                          >
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      ) : null}
                    </article>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      <Dialog
        open={pendingMove !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingMove(null);
          }
        }}
      >
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('partners.pipeline.lostTitle')}</DialogTitle>
            <DialogDescription>{t('partners.pipeline.lostDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="lead-lost-reason">{t('partners.fields.lostReason')}</Label>
            <Input
              id="lead-lost-reason"
              value={lostReason}
              onChange={(event) => setLostReason(event.target.value)}
              placeholder={t('partners.pipeline.lostPlaceholder')}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingMove(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={lostReason.trim() === '' || updateStage.isPending}
              onClick={() => {
                if (pendingMove !== null) {
                  applyMove(pendingMove.partner, pendingMove.target, lostReason.trim());
                }
              }}
            >
              {updateStage.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
